/**
 * Normalizes a gender string to possessive form if needed (e.g., 'womens' -> "women's").
 * @param {string} gender
 * @returns {string}
 */
function normalizeGender(gender) {
  const lowercaseGender = gender?.toLowerCase();

  // only allow these gender values
  if (
    typeof gender !== 'string' ||
    (!lowercaseGender.includes('women') &&
      !lowercaseGender.includes('men') &&
      !lowercaseGender.includes('kid') &&
      !lowercaseGender.includes('toddler') &&
      !lowercaseGender.includes('infant') &&
      !lowercaseGender.includes('baby'))
  ) {
    return '';
  }

  // if the gender already ends in possessive, return as is
  if (gender.endsWith("'s")) {
    return gender;
  }

  // return possessive forms for women and men
  if (gender.endsWith('s') && (lowercaseGender.includes('women') || lowercaseGender.includes('men'))) {
    return `${gender.slice(0, -1)}'s`;
  }

  // return singular forms for toddler and infant
  if (gender.endsWith('s') && (lowercaseGender.includes('toddler') || lowercaseGender.includes('infant'))) {
    return gender.slice(0, -1);
  }

  return gender;
}

/**
 * Removes a gender (normalized to possessive) from the start of a title string.
 * @param {string} title
 * @param {string} gender
 * @returns {string}
 */
function removeGenderFromTitle(title, gender) {
  const normGender = normalizeGender(gender);
  if (!normGender) return title;
  const regex = new RegExp(`^${normGender.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\s+`, 'i');
  return title.replace(regex, '');
}
/**
 * Safely returns the `data` property of an object, or an empty object if not present.
 * @param {Object} obj - The object to extract data from.
 * @returns {Object}
 */
function getData(obj) {
  return obj?.data ?? {};
}

const DEFAULT_VARIATION = 'default';
const IMAGE_URL_KEY = 'imageUrl';
const DATA_IMAGE_URL_KEY = 'image_url';
const BASE_URL = 'https://lifeisgood.com';

/**
 * Extracts an array of values for a given key from a list of variations, with fallback and filtering.
 * Optimized for performance using for loop.
 * @param {Array} variations - Array of variation objects.
 * @param {string} key - Key to extract from each variation's data.
 * @returns {Array}
 */
function getArrayFromVariations(variations, key) {
  if (!Array.isArray(variations)) return [DEFAULT_VARIATION];
  const arr = [];
  let i = 0;
  while (i < variations.length) {
    const value = getData(variations[i])[key] ?? '';
    if (value) arr.push(value);
    i += 1;
  }
  return arr.length ? arr : [DEFAULT_VARIATION];
}

/**
 * Gets the image URL from an object, checking both top-level and data properties.
 * @param {Object} obj - The object to extract image URL from.
 * @param {string} [fallbackKey] - Top-level key for image URL.
 * @param {string} [dataKey] - Data property key for image URL.
 * @returns {string}
 */
function getImage(obj, fallbackKey = IMAGE_URL_KEY, dataKey = DATA_IMAGE_URL_KEY) {
  const data = getData(obj);
  return obj?.[fallbackKey] ?? data[dataKey] ?? '';
}

/**
 * Maps a Constructor.io PLP item to a Shopify-like product object for ProductCard.
 * @param {Object} item - The Constructor.io item to map.
 * @returns {Object}
 */
function mapConstructorProduct(item) {
  if (!item || typeof item !== 'object') return {};

  const data = getData(item);
  const swatches = getArrayFromVariations(item?.variations, 'color');
  const colors = swatches; // colors and swatches are the same logic
  const gender = data.gender ?? '';
  const normalizedGender = normalizeGender(gender);
  const rawTitle = item?.itemName ?? item?.value ?? '';
  const title = removeGenderFromTitle(rawTitle, gender);
  const { price: initialPrice } = data;
  let price = initialPrice;
  if (price == null && Array.isArray(item?.variations) && item.variations.length > 0) {
    price = getData(item.variations[0]).price;
  }

  const variants = Array.isArray(item?.variations)
    ? item.variations.map((variant) => {
        const vData = getData(variant);
        return {
          id: variant?.variationId ?? vData.variation_id ?? '',
          price: vData.price ?? '',
          compare_at_price: vData.compare_at_price ?? '',
          size: vData.size ?? '',
          color: vData.color ?? '',
          hexCode: vData.hex_code ?? '',
          selectedOptions: [{ name: 'Color', value: vData.color ?? '' }],
          saleProduct: vData.sale_product ?? false,
          images: [
            {
              src: getImage(variant),
              alt: variant?.itemName ?? variant?.value ?? '',
            },
          ],
          variant_media_ids: vData.variant_media_ids ?? [],
        };
      })
    : [];

  return {
    id: item?.itemId ?? item?.id ?? '',
    title,
    handle: item?.url?.replace?.(BASE_URL, '') ?? data.url?.replace?.(BASE_URL, '') ?? '',
    images: [
      {
        src: getImage(item),
        alt: rawTitle,
      },
    ],
    media_ids: data.media_ids ?? [],
    media_images: data.media_images ?? [],
    variants,
    price,
    category: normalizedGender,
    gan: data.gan ?? '',
    master_id: data.master_id ?? '',
    isKidsGraphic: !!data.tag_is_kids_graphic,
    isHoodieGraphic: !!data.tag_is_hoodie_graphic,
    isTankGraphic: !!data.tag_is_tank_graphic,
    isDisableCustomizer: !!data.tag_is_disable_customizer,
    isCustomizable: !!data.tag_is_customizable,
    isBundleMSRP: data.tag_bundle_msrp,
    description: item?.description ?? '',
    badge: data.tag_badge ?? '',
    saleProduct: data.tag_sale_product ?? '',
    tags: Array.isArray(item?.groupIds) ? item.groupIds : [],
    swatches,
    colors,
  };
}

export default mapConstructorProduct;
